import React from "react";
import { Search } from "lucide-react";
import { money } from "@/lib/financeApi";
import { field } from "@/components/nds/windows/finance/parts";

export const STATUS_TABS = [
  ["all", "All", () => true],
  ["awaiting", "Awaiting payment", (i) => i.status === "submitted" || i.status === "approved"],
  ["paid", "Paid", (i) => i.status === "paid"],
  ["archived", "Archived / voided", (i) => i.status === "voided"],
  ["draft", "Drafts", (i) => i.status === "draft"],
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const cycleOf = (i) => i.cycle || (i.period_end ? `${MONTHS[Number(i.period_end.slice(5, 7)) - 1]} ${i.period_end.slice(0, 4)}` : "—");
const cycleKey = (c) => { const [m, y] = c.split(" "); return `${y}-${String(MONTHS.indexOf(m)).padStart(2, "0")}`; };

export function applyFilters(invoices, { flow, cycle, q }) {
  const s = q.trim().toLowerCase();
  return invoices.filter((i) => (!flow || i.flow === flow) && (!cycle || cycleOf(i) === cycle)
    && (!s || `${i.vendor_name} ${i.invoice_number}`.toLowerCase().includes(s)));
}

export function InvoiceFilters({ invoices, f, setF }) {
  const cycles = [...new Set(invoices.map(cycleOf))].filter((c) => c !== "—").sort((a, b) => cycleKey(b).localeCompare(cycleKey(a)));
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-nex-muted" />
        <input value={f.q} onChange={set("q")} placeholder="Vendor or invoice #" data-testid="fin-inv-search" className={`${field} pl-7 w-48`} />
      </label>
      <select value={f.cycle} onChange={set("cycle")} data-testid="fin-inv-cycle-filter" className={field}>
        <option value="">All months</option>
        {cycles.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <select value={f.flow} onChange={set("flow")} data-testid="fin-inv-flow-filter" className={field}>
        <option value="">Labor + rent</option>
        <option value="receivable">Billed by TEA (labor)</option>
        <option value="payable">Rent to landlord</option>
      </select>
    </div>
  );
}

export function StatusTabs({ invoices, tab, setTab }) {
  return (
    <div data-testid="fin-inv-status-tabs" className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
      {STATUS_TABS.map(([id, label, match]) => {
        const rows = invoices.filter(match);
        const total = rows.reduce((s, i) => s + i.total_amount, 0);
        const due = rows.filter((i) => i.status !== "voided").reduce((s, i) => s + Math.max(i.balance_due, 0), 0);
        const on = tab === id;
        return (
          <button key={id} type="button" onClick={() => setTab(id)} data-testid={`fin-inv-tab-${id}`} aria-pressed={on}
            className={`min-w-0 sm:min-w-[140px] text-left px-3 py-2 border rounded-sm transition-colors ${on ? "border-nex-gold bg-nex-gold/10" : "border-white/10 hover:border-nex-gold/40"}`}>
            <div className={`font-mono text-[9px] tracking-[0.18em] uppercase ${on ? "text-nex-gold" : "text-nex-muted"}`}>
              {label} · <span data-testid={`fin-inv-tab-${id}-count`}>{rows.length}</span>
            </div>
            <div data-testid={`fin-inv-tab-${id}-total`} className="font-mono text-sm text-nex-silver">{money(total)}</div>
            <div data-testid={`fin-inv-tab-${id}-due`} className={`font-mono text-[10px] ${due < -0.004 ? "text-orange-300" : "text-nex-muted"}`}>{due < -0.004 ? `${money(-due)} overpaid` : `${money(due)} due`}</div>
          </button>
        );
      })}
    </div>
  );
}
